import { useState, useEffect, useRef, useLayoutEffect, useMemo } from 'react';
import { useNavigate, useLocation, Routes, Route, Navigate, useParams, Outlet, useOutletContext, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Car, LogEntry, supabase } from './lib/supabase';
import { dataManager } from './lib/dataManager';
import { useCars, useFavoriteCars, useSharedCars, useCarBySlug, useLogEntries, useGallery, useSpareParts, useNotes, useReminders, CAR_KEYS, LOG_ENTRY_KEYS, GALLERY_KEYS, LOCATION_KEYS, PART_KEYS, NOTE_KEYS, FAVORITE_KEYS } from './hooks/useCarData';
import { AddRecordForm } from './views/AddRecordForm';
import { ExpensesList } from './views/ExpensesList';
import { Statistics2 } from './views/Statistics2';
import { Statistics } from './views/Statistics';
import ExportView from './views/ExportView';
import { Gallery } from './views/Gallery';
import { SparePartsList } from './views/SparePartsList';
import { SparePartModal } from './views/SparePartModal';
import { NotesList } from './views/NotesList';
import { NoteForm } from './views/NoteForm';
import { LocationForm } from './views/LocationForm';
import { LocationsList } from './views/LocationsList';
import { RemindersList } from './views/RemindersList';
import { ReminderForm } from './views/ReminderForm';
import { ExpensesMapView } from './views/ExpensesMapView';
import { DemoView } from './views/DemoView';
import { useLocations } from './hooks/useCarData';
import { HealthCircle } from './components/ui/HealthCircle';
import { LoginForm } from './views/auth/LoginForm';
import { LandingPage } from './views/LandingPage';
import { LandingPageOld } from './views/LandingPageOld';
import { Dashboard } from './views/Dashboard';
import { CarOverview } from './views/CarOverview';
import { ImportExpenses } from './views/ImportExpenses';
import { AdminLayout } from './views/admin/AdminLayout';
import { AdminDashboard } from './views/admin/AdminDashboard';
import { AdminUsers } from './views/admin/AdminUsers';
import { AdminAnalytics } from './views/admin/AdminAnalytics';
import { AdminSupport } from './views/admin/AdminSupport';
import { AdminErrors } from './views/admin/AdminErrors';
import { AdminTests } from './views/admin/AdminTests';
import { AdminJarvisTests } from './views/admin/AdminJarvisTests';
import { AdminAudits } from './views/admin/AdminAudits';
import { TestReporter } from './components/TestReporter';
import { AdminLoginTimes } from './views/admin/AdminLoginTimes';
import { AdminCars } from './views/admin/AdminCars';
import { AdminPricingStats } from './views/admin/AdminPricingStats';
import { AdminCarTasksStats } from './views/admin/AdminCarTasksStats';
import { DefaultExpenseTypesEditor } from './views/admin/DefaultExpenseTypesEditor';
import { Settings } from './views/settings/Settings';
import { SettingsPlans } from './views/settings/SettingsPlans';
import { PaymentSuccess } from './views/settings/PaymentSuccess';
import { ApiSettings } from './views/settings/ApiSettings';
import { ApiDocsView } from './views/settings/ApiDocsView';
import { CarEditPage } from './views/admin/CarEditPage';
import { CarsList } from './views/CarsList';
import { CarForm } from './views/CarForm';
import { ContactUsForm } from './views/settings/ContactUsForm';
import { RepairAnalysis } from './views/RepairAnalysis';
import { CarTasksView } from './views/CarTasksView';
import { CarTaskForm } from './views/CarTaskForm';
import { useAuth } from './contexts/AuthContext';
import { usePreferences } from './contexts/PreferencesContext';
import { CarEmailsView } from './views/emails/CarEmailsView';
import { PrivacyPage } from './views/PrivacyPage';
import { TermsPage } from './views/TermsPage';
import { AlternativesPage } from './views/AlternativesPage';
import { JarvisProvider } from './components/ui/Jarvis/JarvisProvider';
import { JarvisFAB } from './components/ui/Jarvis/JarvisFAB';
import { JarvisChat } from './components/ui/Jarvis/JarvisChat';

import { BlogList } from './views/BlogList';
import { BlogPost } from './views/BlogPost';
import { AuditPage } from './views/AuditPage';
import { AuditCaseStudyView } from './views/AuditCaseStudyView';

import { getLastSelectedCarSlug, setLastSelectedCarSlug } from './lib/lastCarSelection';
import { trackUserActivity } from './lib/activityTracking';

import { estimateCurrentMileage } from './lib/mileageUtils';
import { Search, Car as CarIcon, Star, Settings as SettingsIcon, BarChart2, Plus, Sun, Moon, Menu, X, Home, Globe, MapPin, ChevronDown } from 'lucide-react';
import { LanguagePickerModal } from './components/ui/LanguagePickerModal';
import { SupportedLanguage } from './lib/expenseTypeHelpers';
import { GlobalSearchView } from './views/GlobalSearchView';
import { PageTransition } from './components/ui/PageTransition';
import { NavigationBar } from './components/ui/NavigationBar';
import { ConfirmModal } from './components/ui/ConfirmModal';
import { AlertModal } from './components/ui/AlertModal';
import { theme, lightTheme, darkTheme } from './lib/theme';
import { useTranslations } from './i18n';
const TAB_MAP: Record<string, 'dashboard' | 'expenses' | 'stats' | 'stats_old' | 'export' | 'gallery' | 'spare-parts' | 'notes' | 'locations' | 'stats-map' | 'repair' | 'repair-switch' | 'reminders'> = {
  dashboard: 'dashboard',
  expenses: 'expenses',
  stats: 'stats',
  stats_old: 'stats_old',
  export: 'export',
  gallery: 'gallery',
  parts: 'spare-parts',
  notes: 'notes',
  locations: 'locations',
  'stats/map': 'stats-map',
  repair: 'repair',
  'repair/switch': 'repair-switch',
  reminders: 'reminders'
};

const REV_TAB_MAP: Record<string, string> = {
  dashboard: 'dashboard',
  expenses: 'expenses',
  stats: 'stats',
  stats_old: 'stats_old',
  export: 'export',
  gallery: 'gallery',
  'spare-parts': 'parts',
  notes: 'notes',
  locations: 'locations',
  'stats-map': 'stats/map',
  repair: 'repair',
  'repair-switch': 'repair/switch',
  reminders: 'reminders'
};

const globalScrollPositions: Record<string, Record<string, number>> = {};

function UnsubscribeHandler() {
  const navigate = useNavigate();
  useEffect(() => {
    const timer = setTimeout(() => navigate('/', { replace: true }), 3000);
    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-slate-950 p-6 text-center">
      <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-3xl p-8 space-y-4">
        <h2 className="text-2xl font-black text-white">Unsubscribed</h2>
        <p className="text-slate-400">You have been successfully unsubscribed from daily summary reports.</p>
        <div className="pt-4">
          <button onClick={() => navigate('/')} className="px-6 py-2 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-500 transition-all">
            Back to Home
          </button>
        </div>
        <p className="text-xs text-slate-600">Redirecting in 3 seconds...</p>
      </div>
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function CarRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/${slug}`} replace />;
}



function SubPageRouter({ tabOverride }: { tabOverride?: string }) {
  const { tab: paramTab } = useParams();
  const tab = tabOverride || paramTab;
  if (tab === 'expenses') return <ExpensePage />;
  if (tab === 'parts' || tab === 'spare-parts') return <SparePartPage />;
  if (tab === 'notes') return <NotePage />;
  if (tab === 'locations') return <LocationPage />;
  if (tab === 'reminders') return <ReminderPage />;
  return <Navigate to=".." replace />;
}

function AuthVerifyHandler() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const type = searchParams.get('type') || 'magiclink';
  const email = searchParams.get('email');
  const redirectTo = searchParams.get('redirect_to');
  const lang = searchParams.get('lang');

  const { t } = useTranslations();
  const { preferences, updatePreferences } = usePreferences();

  useEffect(() => {
    if (lang && lang !== preferences?.language) {
      void updatePreferences({ language: lang as any });
    }
  }, [lang, preferences?.language, updatePreferences]);

  useEffect(() => {
    if (token && email) {
      // Use the Cloudflare Worker as the verification engine (Plan C)
      const workerUrl = new URL('https://mycarcosts.gtreulands.workers.dev/auth/verify');
      workerUrl.searchParams.set('token', token);
      workerUrl.searchParams.set('type', type);
      workerUrl.searchParams.set('email', email);
      if (redirectTo) {
        workerUrl.searchParams.set('redirect_to', redirectTo);
      }
      if (lang) {
        workerUrl.searchParams.set('lang', lang);
      }
      // Native redirect to the Worker
      console.log('Redirecting to worker for verification:', workerUrl.toString());
      window.location.href = workerUrl.toString();
    }
  }, [token, email, type, redirectTo, lang]);

  return (
    <div className="min-h-screen bg-gradient-theme flex flex-col items-center justify-center text-center p-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400 mb-4"></div>
      <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t.auth.authenticating}</h2>
      <p className="text-gray-500 dark:text-gray-400">{t.auth.authenticatingDesc}</p>
    </div>
  );
}


// Redirect component to handle root navigation logic with smart defaults
function RootRedirect({ cars, sharedCars, favoriteCars }: { cars: Car[], sharedCars: Car[], favoriteCars: Car[] }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-theme flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
      </div>
    );
  }

  // Not logged in -> Show Landing Page directly (better for SEO)
  if (!user) {
    return <LandingPage />;
  }

  // 0. CHECK PENDING FAVORITES (HIGHEST PRIORITY)
  // If the user was just logging in because they liked a car, send them there first!
  const pendingSlug = localStorage.getItem('pending_favorite_slug');
  if (pendingSlug) {
    return <Navigate to={`/${pendingSlug}`} replace />;
  }

  // 1. Check localStorage for last selection - Priority #1
  const lastSelectedSlug = getLastSelectedCarSlug();
  if (lastSelectedSlug) {
    // Check if it exists in any of our lists (personal, shared, or favorite)
    const exists = [...cars, ...sharedCars, ...favoriteCars].some(c => c.slug === lastSelectedSlug);
    if (exists) {
      return <Navigate to={`/${lastSelectedSlug}`} replace />;
    }
  }

  // 2. Smart auto-navigation for single car scenarios
  const personalActive = cars.filter(c => c.user_id === user.id && !c.is_archived);
  const sharedCount = sharedCars.length;

  // Exactly 1 personal and NO shared -> Auto-navigate
  if (personalActive.length === 1 && sharedCount === 0) {
    return <Navigate to={`/${personalActive[0].slug}`} replace />;
  }

  // NO personal and exactly 1 shared -> Auto-navigate
  if (personalActive.length === 0 && sharedCount === 1) {
    return <Navigate to={`/${sharedCars[0].slug}`} replace />;
  }

  // 3. Fallback to Garage for ambiguity (multiple cars, mixed types, archived only, or favorites only)
  return <Navigate to="/garage" replace />;
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { preferences, loading: preferencesLoading } = usePreferences();
  const queryClient = useQueryClient();
  const { t } = useTranslations();

  // remote data
  const { data: cars = [], isLoading: carsLoading } = useCars(user?.id);
  const { data: favoriteCars = [] } = useFavoriteCars(user?.id);
  const { data: sharedCars = [], isLoading: sharedCarsLoading } = useSharedCars(user?.email);
  
  // local ui state
  const [selectedCar, setSelectedCar] = useState<Car | null>(null); // Still needed for some callback contexts? Or can we derive it?
  // We'll see. The routes use selectedCar in CarLayout.

  const [isImportOpen, setIsImportOpen] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deleteAllTargetId, setDeleteAllTargetId] = useState<string | null>(null);
  const [alertConfig, setAlertConfig] = useState<{ isOpen: boolean; title: string; message: string }>({ isOpen: false, title: '', message: '' });

  const isPublicView = !['/garage', '/settings', '/cars', '/contact', '/login', '/admin'].some(p => location.pathname.startsWith(p)) && location.pathname !== '/';

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    if (!authLoading && preferences?.theme) {
      const isDark = preferences.theme === 'dark';
      const isDarkSet = document.documentElement.classList.contains('dark');

      if (isDark && !isDarkSet) {
        document.documentElement.classList.add('dark');
      } else if (!isDark && isDarkSet) {
        document.documentElement.classList.remove('dark');
      }

      // Force background update for mobile overscroll/safe-areas to prevent old color persistence
      const activeTheme = isDark ? darkTheme : lightTheme;
      const gradientStart = activeTheme.colors.gradientStart;
      const gradientEnd = activeTheme.colors.gradientEnd;

      // Update CSS variables for the gradient dynamically
      document.documentElement.style.setProperty('--bg-start', gradientStart);
      document.documentElement.style.setProperty('--bg-end', gradientEnd);

      // Set html background as gradient to match CSS file
      // BUT keep body transparent so the z-index:-50 gradient overlay is visible
      document.documentElement.style.background = `linear-gradient(135deg, ${gradientStart} 0%, ${gradientEnd} 100%)`;
      document.body.style.backgroundColor = 'transparent';

      // Update meta theme-color for browser UI
      let metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (!metaThemeColor) {
        metaThemeColor = document.createElement('meta');
        metaThemeColor.setAttribute('name', 'theme-color');
        document.head.appendChild(metaThemeColor);
      }
      metaThemeColor.setAttribute('content', gradientStart);
    }
  }, [authLoading, preferences?.theme]);

  // Track user activity on navigation
  useEffect(() => {
    if (user) {
      trackUserActivity('page_view');
    }
  }, [location.pathname, user]);


  const toggleFavorite = async (carId: string, slug: string) => {
    if (!user) {
      localStorage.setItem('pending_favorite_id', carId);
      localStorage.setItem('pending_favorite_slug', slug);
      navigate('/login');
      return;
    }

    const isFav = favoriteCars.some(c => c.id === carId);
    try {
      if (isFav) {
        await dataManager.removeFavorite(user.id, carId);
      } else {
        await dataManager.addFavorite(user.id, carId);
      }
      queryClient.invalidateQueries({ queryKey: ['favorites', user.id] });
    } catch (error) {
      console.error('Error toggling favorite:', error);
      setAlertConfig({ isOpen: true, title: t.common.error, message: 'Failed to update favorite' });
    }
  };

  // Handle pending favorites after login
  useEffect(() => {
    const processPendingFavorite = async () => {
      const pendingId = localStorage.getItem('pending_favorite_id');
      const pendingSlug = localStorage.getItem('pending_favorite_slug');

      if (user && pendingId) {
        // Clear storage IMMEDIATELY to prevent double execution and race conditions
        localStorage.removeItem('pending_favorite_id');
        localStorage.removeItem('pending_favorite_slug');

        console.log('Processing post-login favorite for:', pendingSlug);

        try {
          // Check if already in favorites to avoid duplicates (though DB handles it)
          const alreadyFav = favoriteCars.some(c => c.id === pendingId);
          if (!alreadyFav) {
            // Before adding to DB, we can manually fetch the car to add it optimistically to the list
            // but for simplicity, we just trigger the DB call and invalidate.
            // A more robust fix for "instant" appearance is to invalidate ALL relevant lists.
            await dataManager.addFavorite(user.id, pendingId);
            
            // Critical: Invalidate EVERYTHING related to cars and favorites
            // to force an immediate and complete data sync
            await Promise.all([
               queryClient.invalidateQueries({ queryKey: FAVORITE_KEYS.list(user.id) }),
               queryClient.invalidateQueries({ queryKey: CAR_KEYS.all })
            ]);
          }
          
          // Redirect back to the car page if we're not there yet
          if (pendingSlug) {
             console.log('Redirecting to pending favorite car page:', pendingSlug);
             navigate(`/${pendingSlug}`, { replace: true });
          }
        } catch (err) {
          console.error('Error processing pending favorite:', err);
        }
      }
    };

    if (user) {
      processPendingFavorite();
    }
  }, [user, favoriteCars, navigate, queryClient]);


  const handleCarSaved = () => {
    queryClient.invalidateQueries({ queryKey: CAR_KEYS.all });
    queryClient.invalidateQueries({ queryKey: LOG_ENTRY_KEYS.all });
    queryClient.invalidateQueries({ queryKey: GALLERY_KEYS.all });
    navigate(-1);
  };

  const handleCarDeleted = () => {
    queryClient.invalidateQueries({ queryKey: CAR_KEYS.all });
    navigate('/');
  };

  // Removed handlers that are now in CarLayout or unused


  const performDeleteAllExpenses = async () => {
    if (!deleteAllTargetId) return;
    const carId = deleteAllTargetId;

    const { error } = await supabase
      .from('expenses')
      .delete()
      .eq('car_id', carId);

    if (!error) {
      queryClient.invalidateQueries({ queryKey: LOG_ENTRY_KEYS.list(carId) });
      queryClient.invalidateQueries({ queryKey: GALLERY_KEYS.car(carId) });
      setShowDeleteAllModal(false);
      setDeleteAllTargetId(null);
    } else {
      console.error('Error deleting expenses:', error);
    }
  };

  // handleDeleteAllExpenses is still used by CarEditWrapper route
  const handleDeleteAllExpenses = (carId: string) => {
    setDeleteAllTargetId(carId);
    setShowDeleteAllModal(true);
  };

  // Removed handleExpenseDeleted logic and loadCarBySlug logic as they are now handled by hooks


  if (authLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-gradient-theme">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
      </div>
    );
  }

  // Admin logic handled by AdminLayout route

  // Redirect unauthenticated users from private routes to /landing
  if (!user && !isPublicView && !location.pathname.startsWith('/login')) {
    return <Navigate to="/landing" replace />;
  }

  // Only show full-screen loader on initial data load (no cars or preferences at all)
  const isInitialDataLoading = (carsLoading && !cars.length) || (sharedCarsLoading && !sharedCars?.length) || (user && preferencesLoading && !preferences);

  if (isInitialDataLoading && !isPublicView && !location.pathname.startsWith('/login')) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-gradient-theme pointer-events-none">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
      </div>
    );
  }





  return (
    <JarvisProvider>
      <ScrollToTop />
      <div className="bg-page-gradient" />
      <TestReporter>
        <Routes>
        <Route path="/" element={<RootRedirect cars={cars} sharedCars={sharedCars} favoriteCars={favoriteCars} />} />
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/landing_old" element={<LandingPageOld />} />
        <Route path="/garage" element={<Dashboard cars={cars} favoriteCars={favoriteCars} sharedCars={sharedCars} onCarClick={(car) => { setLastSelectedCarSlug(car.slug); navigate(`/${car.slug}`); }} onAddCar={() => navigate('/cars/new')} />} />
        <Route path="/settings" element={
          <Settings
            onBack={() => {
              // Try going back in history. If we end up at a settings page due to some 
              // state/history quirk, we can use an explicit fallback.
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/', { replace: true });
              }
            }}
          />
        } />
        <Route path="/settings/cars" element={<CarsList cars={cars} onBack={() => navigate(-1)} onEditCar={(car) => navigate(`/${car.slug}/edit`)} onAddCar={() => navigate('/cars/new')} />} />
        <Route path="/settings/plans" element={<SettingsPlans onBack={() => navigate(-1)} />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        {/* <Route path="/settings/api" element={<ApiSettings onBack={() => navigate(-1)} />} /> */}
        <Route path="/docs/api" element={<ApiDocsView onBack={() => navigate(-1)} />} />
        <Route path="/cars/new" element={<CarForm onBack={() => navigate(-1)} onCarSaved={handleCarSaved} />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/alternatives" element={<AlternativesPage />} />
        <Route path="/alternative" element={<AlternativesPage />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:postSlug" element={<BlogPost />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/audit/case-study/:studySlug" element={<AuditCaseStudyView />} />
        <Route path="/cars/:slug/edit" element={
          <CarEditWrapper
            cars={cars}
            sharedCars={sharedCars}
            onBack={() => navigate(-1)}
            onSaved={handleCarSaved}
            onImportClick={(car) => {
              setSelectedCar(car);
              setIsImportOpen(true);
            }}
            onDeleteAllClick={handleDeleteAllExpenses}
            onDeleted={handleCarDeleted}
          />
        }>
          <Route path="emails" element={
            <div className="fixed inset-0 z-[100] bg-transparent">
              <CarEmailsView />
            </div>
          } />
        </Route>

        <Route path="/demo/:view" element={<DemoView />} />

        <Route path="/repair/*" element={<RepairAnalysis standalone={true} car={null} logEntries={[]} isReadOnly={false} />} />

        {/* Car routes */}
        <Route
          path="/:slug"
          element={
            <CarLayout
              isImportOpen={isImportOpen}
              setIsImportOpen={setIsImportOpen}
              isAdminView={false}
              favoriteCars={favoriteCars}
              onToggleFavorite={toggleFavorite}
            />
          }
        >
          <Route index element={<CarDashboard />} />
          <Route path="search" element={<SearchPage />} />
          <Route path="edit" element={
            <CarEditWrapper
              cars={cars}
              sharedCars={sharedCars}
              onBack={() => navigate(-1)}
              onSaved={handleCarSaved}
              onImportClick={(car) => {
                setSelectedCar(car);
                setIsImportOpen(true);
              }}
              onDeleteAllClick={handleDeleteAllExpenses}
              onDeleted={handleCarDeleted}
            />
          }>
            <Route path="emails" element={
              <div className="fixed inset-0 z-[100] bg-transparent">
                <CarEmailsView />
              </div>
            } />
          </Route>
          {/* New sub-routes for direct access */}
          <Route path="expenses">
            <Route index element={<CarDashboard />} />
            <Route path="new" element={<SubPageRouter tabOverride="expenses" />} />
            <Route path=":itemId" element={<SubPageRouter tabOverride="expenses" />} />
          </Route>
          <Route path="parts">
            <Route index element={<CarDashboard />} />
            <Route path="new" element={<SubPageRouter tabOverride="parts" />} />
            <Route path=":itemId" element={<SubPageRouter tabOverride="parts" />} />
          </Route>
          <Route path="notes">
            <Route index element={<CarDashboard />} />
            <Route path="new" element={<SubPageRouter tabOverride="notes" />} />
            <Route path=":itemId" element={<SubPageRouter tabOverride="notes" />} />
          </Route>
          <Route path="locations">
            <Route index element={<CarDashboard />} />
            <Route path="new" element={<SubPageRouter tabOverride="locations" />} />
            <Route path=":itemId" element={<SubPageRouter tabOverride="locations" />} />
          </Route>
          <Route path="reminders">
            <Route index element={<CarDashboard />} />
            <Route path="new" element={<SubPageRouter tabOverride="reminders" />} />
            <Route path=":itemId" element={<SubPageRouter tabOverride="reminders" />} />
          </Route>
          <Route path="stats/*" element={<CarDashboard />} />
          <Route path="stats_old" element={<CarDashboard />} />
          <Route path="export" element={<CarDashboard />} />
          <Route path="gallery" element={<CarDashboard />} />
          <Route path="repair/*" element={<CarDashboard />} />
          <Route path="car-tasks" element={<CarTasksView />} />
          <Route path="car-tasks/:taskId" element={<PageTransition><CarTaskPage /></PageTransition>} />
        </Route>

        {/* Legacy redirect for /car/:slug */}
        <Route path="/car/:slug" element={<CarRedirect />} />
        <Route path="/car/:tab/:slug" element={<CarRedirect />} />

        {/* Global Admin routes */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="cars" element={<AdminCars />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="pricing" element={<AdminPricingStats />} />
          <Route path="car-tasks" element={<AdminCarTasksStats />} />
          <Route path="support" element={<AdminSupport />} />
          <Route path="login-times" element={<AdminLoginTimes />} />
          <Route path="errors" element={<AdminErrors />} />
          <Route path="tests" element={<AdminTests />} />
          <Route path="jarvis-tests" element={<AdminJarvisTests />} />
          <Route path="audits" element={<AdminAudits />} />
          <Route path="types" element={<DefaultExpenseTypesEditor onBack={() => window.history.back()} />} />
        </Route>

        {/* Admin car routes */}
        <Route
          path="/admin/:slug"
          element={
            <CarLayout
              isImportOpen={isImportOpen}
              setIsImportOpen={setIsImportOpen}
              isAdminView={true}
              favoriteCars={favoriteCars}
              onToggleFavorite={toggleFavorite}
            />
          }
        >
          <Route index element={<CarDashboard />} />
          <Route path="search" element={<SearchPage />} />
          <Route path="edit" element={<CarEditPage />} />
          <Route path="expenses">
            <Route index element={<CarDashboard />} />
            <Route path="new" element={<SubPageRouter tabOverride="expenses" />} />
            <Route path=":itemId" element={<SubPageRouter tabOverride="expenses" />} />
          </Route>
          <Route path="parts">
            <Route index element={<CarDashboard />} />
            <Route path="new" element={<SubPageRouter tabOverride="parts" />} />
            <Route path=":itemId" element={<SubPageRouter tabOverride="parts" />} />
          </Route>
          <Route path="notes">
            <Route index element={<CarDashboard />} />
            <Route path="new" element={<SubPageRouter tabOverride="notes" />} />
            <Route path=":itemId" element={<SubPageRouter tabOverride="notes" />} />
          </Route>
          <Route path="locations">
            <Route index element={<CarDashboard />} />
            <Route path="new" element={<SubPageRouter tabOverride="locations" />} />
            <Route path=":itemId" element={<SubPageRouter tabOverride="locations" />} />
          </Route>
          <Route path="reminders">
            <Route index element={<CarDashboard />} />
            <Route path="new" element={<SubPageRouter tabOverride="reminders" />} />
            <Route path=":itemId" element={<SubPageRouter tabOverride="reminders" />} />
          </Route>
          <Route path="stats/*" element={<CarDashboard />} />
          <Route path="stats_old" element={<CarDashboard />} />
          <Route path="export" element={<CarDashboard />} />
          <Route path="gallery" element={<CarDashboard />} />
          <Route path="repair/*" element={<CarDashboard />} />
          <Route path="car-tasks" element={<CarTasksView />} />
          <Route path="car-tasks/:taskId" element={<PageTransition><CarTaskPage /></PageTransition>} />
        </Route>

        <Route path="/contact" element={<PageTransition><ContactUsForm /></PageTransition>} />
        <Route path="/login" element={<PageTransition><LoginForm /></PageTransition>} />
        <Route path="/auth/verify" element={<AuthVerifyHandler />} />
        <Route path="/unsubscribe" element={<UnsubscribeHandler />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes >
      </TestReporter>

      {/* Jarvis UI Components - Only for Admin Plan for now */}
      {preferences?.plan_type === 'admin' && (
        <>
          <JarvisFAB />
          <JarvisChat />
        </>
      )}

      {isImportOpen && selectedCar && (
        <ImportExpenses
          carId={selectedCar.id}
          onClose={() => setIsImportOpen(false)}
          onImportComplete={() => {
            queryClient.invalidateQueries({ queryKey: LOG_ENTRY_KEYS.list(selectedCar.id) });
            queryClient.invalidateQueries({ queryKey: GALLERY_KEYS.car(selectedCar.id) });
          }}
        />
      )}

      <ConfirmModal
        isOpen={showDeleteAllModal}
        title="Delete All Expenses"
        message="Are you sure you want to delete ALL expenses for this car? This action is irreversible."
        confirmLabel="Delete All"
        cancelLabel="Cancel"
        onConfirm={performDeleteAllExpenses}
        onCancel={() => {
          setShowDeleteAllModal(false);
          setDeleteAllTargetId(null);
        }}
        onClose={() => {
          setShowDeleteAllModal(false);
          setDeleteAllTargetId(null);
        }}
        isDestructive={true}
      />
      <AlertModal
        isOpen={alertConfig.isOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        confirmLabel={t.common.ok}
        onClose={() => setAlertConfig({ isOpen: false, title: '', message: '' })}
      />
    </JarvisProvider>
  );
}

function CarLayout({
  isImportOpen, setIsImportOpen, isAdminView = false,
  favoriteCars = [], onToggleFavorite
}: any) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, isAdmin } = useAuth();
  const { preferences } = usePreferences();
  const queryClient = useQueryClient();
  const { t } = useTranslations();

  const { data: selectedCar, isLoading: carLoading } = useCarBySlug(slug);
  const { data: logEntries = [], isLoading: logEntriesLoading, isFetching: logEntriesFetching } = useLogEntries(selectedCar?.id);

  const { user: authUser } = useAuth();
  const { data: sharedCarsForLayout = [] } = useSharedCars(authUser?.email);
  const { updatePreferences } = usePreferences();
  const [showMenu, setShowMenu] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === 'true';

  // Determine active tab from location
  const getActiveTabFromPath = () => {
    const p = location.pathname.replace(/\/admin\//, '/').replace(/^\//, '');
    const parts = p.split('/');
    if (parts.length <= 1) return 'dashboard';

    // slug is parts[0]
    // tab is parts[1]
    // sub-tab is parts[2]

    if (parts[2]) {
      const fullPath = `${parts[1]}/${parts[2]}`;
      if (TAB_MAP[fullPath]) return TAB_MAP[fullPath];
    }

    const secondPart = parts[1];
    return TAB_MAP[secondPart] || 'dashboard';
  };

  const activeTab = getActiveTabFromPath();


  const setActiveTab = (newTab: 'dashboard' | 'expenses' | 'stats' | 'stats_old' | 'export' | 'gallery' | 'spare-parts' | 'notes' | 'locations' | 'stats-map' | 'repair' | 'repair-switch' | 'reminders') => {
    const urlTab = REV_TAB_MAP[newTab];
    const prefix = isAdminView ? '/admin' : '';
    const path = urlTab === 'dashboard' ? `${prefix}/${slug}` : `${prefix}/${slug}/${urlTab}`;

    // Normal logic: Replace for sub-tabs, Push for main -> sub
    // Custom logic: Push for repair -> repair-switch to enable back navigation
    const isRepairTransition = (activeTab === 'repair' && newTab === 'repair-switch') || 
                              (activeTab === 'repair-switch' && newTab === 'repair');
    
    const shouldReplace = (activeTab !== 'dashboard' && !isRepairTransition) || newTab === 'dashboard';

    navigate(path, { replace: shouldReplace });
    localStorage.setItem('last_active_tab', newTab);
  };

  const [ownerPreferences, setOwnerPreferences] = useState<any>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const prevPathRef = useRef(location.pathname);

  // LIFTED HOOKS: Must be before ANY conditional early returns
  const [statsYear, setStatsYear] = useState<number | 'all'>(() => {
    const saved = sessionStorage.getItem('stats_selectedYear');
    if (saved === 'all') return 'all';
    if (saved) return parseInt(saved);
    return 'all';
  });

  const availableYears = useMemo(() => {
    if (!logEntries) return [new Date().getFullYear()];
    const years = [...new Set(logEntries.map(e => new Date(e.date).getFullYear()))].sort((a, b) => b - a);
    return years.length > 0 ? years : [new Date().getFullYear()];
  }, [logEntries]);

  const estimatedMileage = useMemo(() => {
    if (!selectedCar) return 0;
    return estimateCurrentMileage(selectedCar, logEntries);
  }, [selectedCar, logEntries]);

  useEffect(() => {
    sessionStorage.setItem('stats_selectedYear', statsYear.toString());
  }, [statsYear]);

  useEffect(() => {
    if (selectedCar) {
      dataManager.fetchUserPreferences(selectedCar.user_id)
        .then(setOwnerPreferences)
        .catch(err => console.error('Error fetching owner preferences:', err));
    }
  }, [selectedCar?.id]);

  // Highlight logic: clear highlight after a short delay
  useEffect(() => {
    let timer: any;
    if (highlightId) {
      timer = setTimeout(() => {
        setHighlightId(null);
      }, 1200); // 1.2s to be safe
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [highlightId]);

  useEffect(() => {
    const currentPath = location.pathname;
    const prevPath = prevPathRef.current;

    // Detect if we just returned from an item page to its respective list
    if (prevPath && Object.values(TAB_MAP).some(tab => currentPath.endsWith(`/${tab}`))) {
      const match = prevPath.match(/\/([^/]+)\/([^/]+)$/);
      if (match && Object.values(TAB_MAP).includes(match[1] as any) && match[2] !== 'new' && match[2] !== 'search') {
        setHighlightId(match[2]);
      }
    }
    prevPathRef.current = currentPath;
  }, [location.pathname]);



  if (carLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-gradient-theme">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
      </div>
    );
  }

  if (!selectedCar) {
    return (
      <div className={`min-h-screen ${theme.colors.pageBg} pt-14 flex items-center justify-center px-4 font-primary`}>
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t.cars.carNotFound}</h2>
          <p className="text-gray-600 mb-6">{t.cars.carNotFoundDesc}</p>
          <button onClick={() => navigate('/')} className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
            {t.cars.backToHome}
          </button>
        </div>
      </div>
    );
  }

  if (!selectedCar.is_public && !user) {
    return (
      <div className={`min-h-screen ${theme.colors.pageBg} pt-14 flex items-center justify-center px-4 font-primary`}>
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t.cars.accessDenied}</h2>
          <p className="text-gray-600 mb-6">{t.cars.accessDeniedDesc}</p>
          <button onClick={() => navigate('/')} className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
            {t.cars.backToHome}
          </button>
        </div>
      </div>
    );
  }

  const isOwner = user && selectedCar?.user_id === user.id;
  const isCarAdmin = !isOwner && sharedCarsForLayout.some((c: any) => c.id === selectedCar?.id);

  // CRITICAL CHANGE: Admin rights only active in explicit /admin routes
  const isReadOnly = !isOwner && !isCarAdmin && !isAdminView;

  // Security: If trying to access admin view, must be a system admin
  if (isAdminView && !isAdmin && !authLoading) {
    return <Navigate to={`/${slug}`} replace />;
  }
  const isCarArchived = !!(selectedCar?.is_archived);
  const effectiveUserId = isAdmin && selectedCar ? selectedCar.user_id : (user?.id || null);
  const isFavorite = selectedCar && favoriteCars.some((c: any) => c.id === selectedCar.id);

  const handleExpenseAdded = async (newExpense: any) => {
    if (!selectedCar || !newExpense?.id) return;
    
    // 1. Optimistic Instant Update: Inject raw log entry right away so list UI renders instantly
    queryClient.setQueryData(LOG_ENTRY_KEYS.list(selectedCar.id), (oldList: any) => {
      if (!oldList) return [newExpense];
      const existingIndex = oldList.findIndex((item: any) => item.id === newExpense.id);
      const newList = [...oldList];

      if (existingIndex !== -1) {
        newList[existingIndex] = { ...newList[existingIndex], ...newExpense };
      } else {
        // Only append optimistically if it's new
        newList.unshift({ ...newExpense, expenses: [], log_entry_parts: [], location: null, imageCount: 0, partCount: 0 });
      }
      return newList.sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
    });
    
    // Background invalidate Car softly so mileage updates in the header
    queryClient.invalidateQueries({ queryKey: CAR_KEYS.slug(selectedCar.slug) });

    // 2. Fetch fully enriched log entry in the background to get correctly populated parts/locations/expenses
    const { data: fullExpense } = await supabase
        .from('log_entries')
        .select(`
            *,
            expenses (*),
            location:locations (*),
            log_entry_parts(spare_part_id, spare_parts(name, manufacturer))
        `)
        .eq('id', newExpense.id)
        .single();

    if (fullExpense) {
      const formattedExpense = {
          ...fullExpense,
          location: Array.isArray(fullExpense.location) ? fullExpense.location[0] : fullExpense.location,
          imageCount: fullExpense.image_count || 0,
          partCount: fullExpense.log_entry_parts?.length || 0
      };

      // 3. Patch specific cache item with the enriched data
      queryClient.setQueryData(LOG_ENTRY_KEYS.list(selectedCar.id), (oldList: any) => {
        if (!oldList) return [formattedExpense];
        const existingIndex = oldList.findIndex((item: any) => item.id === formattedExpense.id);
        const newList = [...oldList];

        if (existingIndex !== -1) {
          newList[existingIndex] = formattedExpense;
        }
        return newList;
      });
    }
  };

  const handleExpenseDeleted = () => {
    if (!selectedCar) return;
    queryClient.invalidateQueries({ queryKey: LOG_ENTRY_KEYS.list(selectedCar.id) });
    queryClient.invalidateQueries({ queryKey: CAR_KEYS.slug(selectedCar.slug) });
    // Intentionally skipped aggressive invalidations for gallery, parts, notes
  };


  const contextValue = {
    selectedCar,
    isReadOnly,
    isCarArchived,
    highlightId,
    setHighlightId,
    logEntries,
    logEntriesLoading,
    logEntriesFetching,
    handleExpenseDeleted,
    handleExpenseAdded,
    activeTab,
    setActiveTab,
    isImportOpen,
    setIsImportOpen,
    effectiveUserId,
    isAdmin,
    isAdminView,
    isOwner,
    isCarAdmin,
    statsYear,
    setStatsYear,
    availableYears,
    estimatedMileage,
    saveScrollPosition: (tabName: string, y: number) => {
      if (!globalScrollPositions[selectedCar?.slug || '']) {
        globalScrollPositions[selectedCar?.slug || ''] = {};
      }
      globalScrollPositions[selectedCar?.slug || ''][tabName] = y;
    },
    getScrollPosition: (tabName: string) => {
      const pos = (selectedCar?.slug && globalScrollPositions[selectedCar.slug] && globalScrollPositions[selectedCar.slug][tabName]) || 0;

      return pos;
    },
    isFavorite,
    onToggleFavorite,
    ownerPreferences: isOwner ? (preferences || ownerPreferences) : (ownerPreferences || preferences),
    setShowMenu,
    setShowLanguageModal,
    isPreview
  };

  return (
    <div className={isAdminView ? 'min-h-screen border-[12px] border-slate-950 pointer-events-none relative' : ''}>
      <div className={isAdminView ? 'pointer-events-auto' : ''}>
        <Outlet context={contextValue} />
      </div>
      {isAdminView && (
        <div className="fixed top-0 left-0 right-0 h-[12px] bg-slate-950 z-[100] flex items-center justify-center pointer-events-none">
          <div className="px-3 py-0.5 bg-red-600 text-[10px] font-black text-white uppercase tracking-widest rounded-b-lg shadow-lg">
            Admin Mode
          </div>
        </div>
      )}

      {/* MENU MODAL */}
      {showMenu && (
         <div className="fixed inset-0 z-[100] flex items-start justify-start">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowMenu(false)} />
            <div className="relative w-[280px] h-full bg-white dark:bg-slate-900 shadow-2xl animate-in slide-in-from-left duration-300">
               <div className="p-6 border-b border-gray-100 dark:border-white/5 flex items-center justify-between">
                  <h2 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-widest">{t.settings?.title || 'Menu'}</h2>
                  <button onClick={() => setShowMenu(false)} className="text-gray-400"><X size={20} /></button>
               </div>
               <div className="p-4 space-y-2">
                  <button 
                     onClick={() => { navigate('/landing'); setShowMenu(false); }}
                     className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-gray-300 transition-colors"
                  >
                     <div className="p-2 bg-blue-50 dark:bg-blue-900/40 text-blue-600 rounded-lg"><Home size={20} /></div>
                     <span className="text-sm font-bold">{t.cars?.returnHome || 'Home'}</span>
                  </button>
                  
                  <button 
                     onClick={() => { setShowLanguageModal(true); setShowMenu(false); }}
                     className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-gray-300 transition-colors"
                  >
                     <div className="p-2 bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 rounded-lg"><Globe size={20} /></div>
                     <span className="text-sm font-bold">{t.settings?.language || 'Language'}</span>
                  </button>
                  
                  <button 
                     onClick={() => { 
                        updatePreferences({ theme: preferences?.theme === 'light' ? 'dark' : 'light' }); 
                        setShowMenu(false); 
                     }}
                     className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-gray-300 transition-colors"
                  >
                     <div className="p-2 bg-amber-50 dark:bg-amber-900/40 text-amber-600 rounded-lg">
                        {preferences?.theme === 'light' ? <Sun size={20} /> : <Moon size={20} />}
                     </div>
                     <span className="text-sm font-bold">{t.settings?.interfaceTheme || 'Design'}</span>
                  </button>
               </div>
            </div>
         </div>
      )}

      {/* LANGUAGE PICKER */}
      <LanguagePickerModal 
         isOpen={showLanguageModal}
         title={t.settings?.language || 'Interface Language'}
         currentLanguage={(preferences?.language as SupportedLanguage) || 'en'}
         onClose={() => setShowLanguageModal(false)}
         onSelect={(lang: SupportedLanguage) => {
            updatePreferences({ language: lang });
            setShowLanguageModal(false);
         }}
      />
    </div>
  );
}

function CarDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { t } = useTranslations();
  const context = useOutletContext<any>();
  const {
    selectedCar, isReadOnly, isCarArchived, highlightId,
    logEntries, logEntriesLoading, setActiveTab, activeTab, isAdminView,
    saveScrollPosition, getScrollPosition,
    isFavorite, onToggleFavorite,
    setShowMenu,
    isPreview,
    statsYear,
    setStatsYear,
    availableYears
  } = context;

  const dashboardScrollRef = useRef<HTMLDivElement>(null);
  const overlayScrollRef = useRef<HTMLDivElement>(null);
  const restoredTabsRef = useRef<Set<string>>(new Set());
  const [healthStatus, setHealthStatus] = useState<{ score: number; recommendation: string } | null>(null);

  // Clear restored status when car changes
  useEffect(() => {
    restoredTabsRef.current.clear();
  }, [selectedCar.id]);

  // Data fetching using hooks
  const { data: galleryImagesRaw = [], isLoading: galleryLoading } = useGallery(selectedCar.id);
  const { data: notes = [], isLoading: notesLoading } = useNotes(selectedCar.id);
  const { data: parts = [], isLoading: partsLoading } = useSpareParts(selectedCar.id);
  const { data: locations = [], isLoading: locationsLoading } = useLocations(selectedCar.id);
  const { data: reminders = [], isLoading: remindersLoading } = useReminders(selectedCar.id);

  const galleryImages = galleryImagesRaw.map(img => ({
    url: img.image_url,
    thumbnail: img.image_url_thumb || img.image_url,
    comment: img.comment,
    type: img.type,
    expense_id: (img as any).expense_id,
    spare_part_id: (img as any).spare_part_id
  }));

  const dashboardLoading = galleryLoading || notesLoading || partsLoading || locationsLoading || remindersLoading;

  useLayoutEffect(() => {
    const container = activeTab === 'dashboard' ? dashboardScrollRef.current : overlayScrollRef.current;
    if (!container) return;

    // Only restore once per tab session to avoid late data loads hijacking manual scroll
    if (restoredTabsRef.current.has(activeTab)) return;

    // Wait for initial data if it's the first render
    if (logEntriesLoading && logEntries.length === 0) return;

    const savedY = getScrollPosition(activeTab);
    container.scrollTo({ top: savedY, behavior: 'instant' });
    restoredTabsRef.current.add(activeTab);

    const handleScroll = () => {
      // Don't save 0 if loding or container hasn't sized yet
      if (container.scrollTop === 0 && (logEntries.length === 0 || container.scrollHeight < container.clientHeight + 100)) {
        return;
      }
      saveScrollPosition(activeTab, container.scrollTop);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [activeTab, logEntriesLoading]);

  return (
    <div className={`fixed inset-0 flex flex-col h-full bg-transparent overflow-hidden ${isPreview ? 'preview-mode' : ''}`}>
      {/* Navigation Bar for Dashboard (Main View) */}
      {activeTab === 'dashboard' && (
        <div className="flex-shrink-0 z-50">
          <NavigationBar
            title=""
            leftAction={user ? {
              icon: CarIcon,
              onClick: () => navigate('/garage'),
              className: 'text-blue-600 dark:text-blue-400'
            } : {
              icon: Menu,
              onClick: () => setShowMenu(true),
              className: 'text-blue-600 dark:text-blue-400'
            }}
            fixed={false}
            secondaryRightAction={user ? {
              icon: Search,
              onClick: () => navigate('search')
            } : undefined}

            rightAction={
              isReadOnly && !isAdminView ? [
                {
                  icon: BarChart2,
                  iconClassName: '-scale-x-100',
                  onClick: () => setActiveTab('stats'),
                  className: 'text-blue-600 dark:text-blue-400'
                },
                {
                  icon: Star,
                  onClick: () => onToggleFavorite(selectedCar.id, selectedCar.slug),
                  className: isFavorite ? 'fill-current text-yellow-500' : 'text-blue-600 dark:text-blue-400'
                },
                ...(user ? [{
                  icon: SettingsIcon,
                  onClick: () => navigate('/settings'),
                  className: 'text-blue-600 dark:text-blue-400'
                }] : [])
              ] : (user ? [
                {
                  icon: BarChart2,
                  iconClassName: '-scale-x-100',
                  onClick: () => setActiveTab('stats'),
                  className: 'text-blue-600 dark:text-blue-400'
                },
                {
                  icon: SettingsIcon,
                  onClick: () => navigate('/settings'),
                  className: 'text-blue-600 dark:text-blue-400'
                }
              ] : undefined)
            }
            className={isAdminView ? 'bg-red-600/90 !text-white border-red-700' : ''}
          />
        </div>
      )}

      {/* Dashboard Content - Always Rendered in Background */}
      <div
        className={`flex-1 overflow-y-auto no-scrollbar scroll-smooth relative ${activeTab !== 'dashboard' ? 'hidden' : ''}`}
        ref={dashboardScrollRef}
      >
        <div className={`max-w-2xl mx-auto ${isPreview ? '' : 'pt-4'}`}>
          <CarOverview
            car={selectedCar}
            logEntries={logEntries}
            loading={(logEntriesLoading || dashboardLoading) && logEntries.length === 0}
            galleryImages={galleryImages}
            notes={notes}
            parts={parts}
            locations={locations}
            reminders={reminders}
            isPublicView={isReadOnly}
            isPreview={isPreview}
            onTabChange={setActiveTab}
          />
        </div>
      </div>

      {/* Overlay Views (Navigation Stack Simulation) */}
      {activeTab !== 'dashboard' && (
        <div
          className="fixed inset-0 z-[60] flex flex-col animate-in slide-in-from-right duration-300"
        >


          {/* Overlay Navigation Bar */}
          {activeTab !== 'export' && (
            <div className="relative z-10 flex-shrink-0">
              <NavigationBar
                title={
                  activeTab === 'expenses' ? t.tabs.expenses :
                    activeTab === 'stats' ? t.tabs.stats :
                      activeTab === 'stats-map' ? (t.statistics as any).expensesOnMap || 'Izdevumi kartē' :
                        activeTab === 'gallery' ? t.tabs.gallery :
                          activeTab === 'spare-parts' ? t.tabs.parts :
                            activeTab === 'notes' ? t.tabs.notes :
                              activeTab === 'locations' ? (t.tabs as any).locations || 'Lokācijas' :
                                activeTab === 'repair' ? (t.repairAnalysis as any).title || 'Repair Analysis' :
                                  activeTab === 'repair-switch' ? (t.repairAnalysis as any).switchingTitle || 'Car Switch' :
                                    activeTab === 'export' ? ((t.export as any).title || 'Eksportēt datus') : ''
                }
                onBack={() => {
                  if (activeTab === 'repair-switch') {
                    setActiveTab('repair');
                  } else if ((window.history.state as any)?.idx > 0 || window.history.length > 2) {
                    navigate(-1);
                  } else {
                    setActiveTab('dashboard');
                  }
                }}
                 rightContent={
                   activeTab === 'repair' && healthStatus ? (
                     <div className="flex items-center bg-blue-600/5 dark:bg-white/5 p-1 rounded-full border border-blue-600/5 dark:border-white/5 shadow-sm animate-in fade-in zoom-in duration-300">
                      <HealthCircle 
                        score={healthStatus.score} 
                        size={40} 
                        strokeWidth={4} 
                        innerRadius={14} 
                        showText={true} 
                        fontSize="11px" 
                      />
                     </div>
                   ) : (activeTab === 'stats' ? (
                     <div className="flex items-center gap-2 mr-1.5">
                       <button
                         onClick={() => setActiveTab('stats-map')}
                         className="p-2.5 bg-white dark:bg-slate-900/60 border border-gray-200 dark:border-white/10 rounded-xl text-blue-500 shadow-sm hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                         title={(t.statistics as any).expensesOnMap || 'Izdevumi kartē'}
                       >
                         <MapPin size={18} />
                       </button>
                       <div className="relative">
                         <select
                           value={statsYear}
                           onChange={e => setStatsYear(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                           className="appearance-none pl-3 pr-8 py-2.5 bg-white dark:bg-slate-900/60 border border-gray-200 dark:border-white/10 rounded-xl text-xs font-bold text-gray-800 dark:text-gray-100 cursor-pointer shadow-sm min-w-[80px]"
                         >
                           <option value="all">{t.statistics.allYears}</option>
                           {availableYears.map((y: number) => <option key={y} value={y}>{y}</option>)}
                         </select>
                         <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                       </div>
                     </div>
                   ) : null)
                 }
                fixed={false}
                rightAction={!isReadOnly && !isCarArchived && (activeTab === 'expenses' || activeTab === 'notes' || activeTab === 'spare-parts' || activeTab === 'locations' || activeTab === 'reminders') ? {
                  icon: Plus,
                  onClick: () => {
                    const prefix = isAdminView ? '/admin' : '';
                    const segment = activeTab === 'spare-parts' ? 'parts' : 
                                   (activeTab === 'expenses' ? 'expenses' : 
                                   (activeTab === 'locations' ? 'locations' : 
                                   (activeTab === 'reminders' ? 'reminders' : 'notes')));
                    navigate(`${prefix}/${selectedCar.slug}/${segment}/new`);
                  }
                } : undefined}
                className={isAdminView ? '!bg-red-600 !text-white' : ''}
              />
            </div>
          )}

          {/* Overlay Content */}
          <div className="relative z-10 flex-1 overflow-y-auto no-scrollbar" ref={overlayScrollRef}>
            {logEntriesLoading && logEntries.length === 0 ? (
              <div className="flex flex-col items-center justify-center min-h-[50vh] font-primary">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4" />
                <p className="text-gray-500 font-semibold italic">{t.common?.loading || 'Notiek ielāde...'}</p>
              </div>
            ) : activeTab === 'export' ? (
              <ExportView logEntries={logEntries} onClose={() => {
                if ((window.history.state as any)?.idx > 0 || window.history.length > 2) {
                  navigate(-1);
                } else {
                  setActiveTab('dashboard');
                }
              }} />
            ) : (
              <div className={`max-w-2xl mx-auto ${activeTab === 'stats-map' || activeTab === 'stats' ? '' : 'pt-4'} pb-20`}>
                {activeTab === 'expenses' && (
                  <ExpensesList
                    car={selectedCar}
                    logEntries={logEntries}
                    loading={logEntriesLoading}
                    isPublicView={isReadOnly}
                    highlightId={highlightId}
                  />
                )}
                 {activeTab === 'stats' && (
                   <Statistics2 
                     logEntries={logEntries} 
                     selectedYear={statsYear} 
                   />
                 )}
                {activeTab === 'stats-map' && <ExpensesMapView logEntries={logEntries} car={selectedCar} isPublicView={isReadOnly} />}
                {activeTab === 'stats_old' && <Statistics logEntries={logEntries} />}
                {activeTab === 'gallery' && <Gallery carId={selectedCar.id} />}
                {activeTab === 'spare-parts' && <SparePartsList carId={selectedCar.id} parts={parts} loading={partsLoading} highlightId={highlightId} />}
                {activeTab === 'notes' && <NotesList car={selectedCar} notes={notes} loading={notesLoading} isPublic={isReadOnly} highlightId={highlightId} />}
                {activeTab === 'locations' && <LocationsList car={selectedCar} isPublic={isReadOnly} highlightId={highlightId} />}
                {activeTab === 'reminders' && <RemindersList carId={selectedCar.id} isPublic={isReadOnly} />}
                {activeTab === 'repair' && <RepairAnalysis car={selectedCar} logEntries={logEntries} isReadOnly={isReadOnly} initialView="health" onStatusUpdate={setHealthStatus} />}
                {activeTab === 'repair-switch' && <RepairAnalysis car={selectedCar} logEntries={logEntries} isReadOnly={isReadOnly} initialView="switch" />}
              </div>
            )}
          </div >
        </div >
      )
      }
    </div >
  );
}

function CarTaskPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const context = useOutletContext<any>();
  const { selectedCar } = context;

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleClose = () => {
    if ((window.history.state as any)?.idx > 0 || window.history.length > 2) {
      navigate(-1);
    } else {
      const prefix = location.pathname.startsWith('/admin') ? '/admin' : '';
      navigate(`${prefix}/${selectedCar.slug}/car-tasks`, { replace: true });
    }
  };

  return (
    <CarTaskForm
      carId={selectedCar.id}
      taskId={taskId === 'new' ? 'new' : (taskId || null)}
      onClose={handleClose}
    />
  );
}

function ExpensePage() {
  const { itemId: logEntryId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const context = useOutletContext<any>() || {};
  const { logEntries, selectedCar, isReadOnly, isCarArchived, isAdminView, handleExpenseAdded, logEntriesLoading } = context;

  const listLogEntry = logEntries?.find((e: LogEntry) => e.id === logEntryId);
  const [fetchedLogEntry, setFetchedLogEntry] = useState<LogEntry | null>(null);
  const [fetchingSingle, setFetchingSingle] = useState(false);
  const [fetchAttempted, setFetchAttempted] = useState(false);

  useEffect(() => {
    const fetchSingleLogEntry = async () => {
      if (!logEntryId) return;

      console.log('LogEntry not in list, fetching individually...', logEntryId);
      setFetchingSingle(true);

      try {
        const { data, error } = await supabase
          .from('log_entries')
          .select('*, expenses(*), log_entry_images(id), log_entry_parts(spare_part_id)')
          .eq('id', logEntryId)
          .single();

        if (error) {
          console.error('Error fetching single log entry:', error);
        } else if (data) {
          const transformed = {
            ...data,
            imageCount: data.log_entry_images?.length || 0,
            partCount: data.log_entry_parts?.length || 0
          };
          console.log('Single log entry fetched:', transformed);
          setFetchedLogEntry(transformed);
        }
      } catch (err) {
        console.error('Exception fetching single log entry:', err);
      } finally {
        setFetchingSingle(false);
        setFetchAttempted(true);
      }
    };

    if (logEntryId && !listLogEntry && !logEntriesLoading && !fetchedLogEntry && !fetchingSingle && !fetchAttempted) {
      fetchSingleLogEntry();
    }
  }, [logEntryId, listLogEntry, logEntriesLoading, fetchedLogEntry, fetchingSingle, fetchAttempted]);



  useLayoutEffect(() => {
    // Always start sub-pages at the top
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  if (logEntriesLoading || fetchingSingle || (logEntryId && !listLogEntry && !fetchedLogEntry && !fetchAttempted)) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-gradient-theme">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
      </div>
    );
  }

  const logEntry = listLogEntry || fetchedLogEntry;

  const handleClose = () => {
    if ((window.history.state as any)?.idx > 0 || window.history.length > 2) {
      navigate(-1);
    } else {
      const prefix = location.pathname.startsWith('/admin') ? '/admin' : '';
      navigate(`${prefix}/${selectedCar.slug}/expenses`, { replace: true });
    }
  };

  return (
    <AddRecordForm
      carId={selectedCar?.id}
      logEntry={logEntry}
      allLogEntries={logEntries}
      mode={logEntryId ? 'edit' : 'add'}
      onClose={handleClose}
      onExpenseAdded={(newExpense) => {
        handleExpenseAdded(newExpense);
        // If we added/updated an expense, invalidating queries is handled by parent,
        // but we might want to update local state or just let parent reload loop handle it.
      }}
      isReadOnly={isAdminView ? isReadOnly : (isReadOnly || isCarArchived)}
    />
  );
}

function SparePartPage() {
  const { itemId: partId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const context = useOutletContext<any>();
  const { selectedCar, isReadOnly, isCarArchived, isAdminView } = context;

  const [part, setPart] = useState<any>(null);

  useLayoutEffect(() => {
    // Always start sub-pages at the top
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    if (partId && partId !== 'new') {
      supabase.from('spare_parts').select('*, spare_part_images(*)').eq('id', partId).single().then(({ data }) => {
        if (data) {
          const partWithImages = {
            ...data,
            images: data.spare_part_images?.sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0)).map((img: any) => ({
              url: img.image_url,
              thumbnail: img.image_url_thumb,
              comment: img.comment,
              log_entry_id: img.log_entry_id,
              note_id: img.note_id
            }))
          };
          setPart(partWithImages);
        }
      });
    } else {
      setPart(null);
    }
  }, [partId]);

  if (partId && partId !== 'new' && !part) {
    return (
      <div className="fixed inset-0 flex items-center justify-center font-primary z-50 bg-gradient-theme">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
      </div>
    );
  }

  const handleClose = () => {
    if ((window.history.state as any)?.idx > 0 || window.history.length > 2) {
      navigate(-1);
    } else {
      const prefix = location.pathname.startsWith('/admin') ? '/admin' : '';
      navigate(`${prefix}/${selectedCar.slug}/parts`, { replace: true });
    }
  };

  return (
    <SparePartModal
      onClose={handleClose}
      onSaved={handleClose}
      carId={selectedCar.id}
      sparePart={partId === 'new' ? null : part}
      isReadOnly={isAdminView ? isReadOnly : (isReadOnly || isCarArchived)}
    />
  );
}

function NotePage() {
  const { itemId: noteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const context = useOutletContext<any>();
  const { selectedCar, isReadOnly, isCarArchived, isAdminView } = context;

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleClose = () => {
    if ((window.history.state as any)?.idx > 0 || window.history.length > 2) {
      navigate(-1);
    } else {
      const prefix = location.pathname.startsWith('/admin') ? '/admin' : '';
      navigate(`${prefix}/${selectedCar.slug}/notes`, { replace: true });
    }
  };

  return (
    <NoteForm
      carId={selectedCar.id}
      noteId={noteId === 'new' ? undefined : noteId}
      onClose={handleClose}
      isReadOnly={isAdminView ? isReadOnly : (isReadOnly || isCarArchived)}
    />
  );
}

function LocationPage() {
  const { itemId: locationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const context = useOutletContext<any>();
  const { selectedCar, isReadOnly, isCarArchived, isAdminView } = context;

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleClose = () => {
    if ((window.history.state as any)?.idx > 0 || window.history.length > 2) {
      navigate(-1);
    } else {
      const prefix = location.pathname.startsWith('/admin') ? '/admin' : '';
      navigate(`${prefix}/${selectedCar.slug}/locations`, { replace: true });
    }
  };

  return (
    <LocationForm
      carId={selectedCar.id}
      locationId={locationId === 'new' ? undefined : locationId}
      onClose={handleClose}
      isReadOnly={isAdminView ? isReadOnly : (isReadOnly || isCarArchived)}
    />
  );
}

function ReminderPage() {
  const { itemId: reminderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const context = useOutletContext<any>();
  const { selectedCar, isReadOnly, isCarArchived, isAdminView } = context;

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleClose = () => {
    if ((window.history.state as any)?.idx > 0 || window.history.length > 2) {
      navigate(-1);
    } else {
      const prefix = location.pathname.startsWith('/admin') ? '/admin' : '';
      navigate(`${prefix}/${selectedCar.slug}/reminders`, { replace: true });
    }
  };

  return (
    <ReminderForm
      carId={selectedCar.id}
      reminderId={reminderId === 'new' ? undefined : reminderId}
      onClose={handleClose}
      isReadOnly={isAdminView ? isReadOnly : (isReadOnly || isCarArchived)}
    />
  );
}

function SearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const context = useOutletContext<any>();
  const { selectedCar, activeTab, logEntries } = context;

  const query = searchParams.get('q') || '';

  return (
    <GlobalSearchView
      carId={selectedCar.id}
      slug={selectedCar.slug}
      activeTab={activeTab}
      logEntries={logEntries}
      onClose={() => navigate('..')}
      query={query}
      onQueryChange={(q) => setSearchParams({ q }, { replace: true })}
    />
  );
}

function CarEditWrapper({
  cars, sharedCars, onBack, onSaved, onImportClick, onDeleteAllClick, onDeleted
}: {
  cars: Car[],
  sharedCars: Car[],
  onBack: () => void,
  onSaved: () => void,
  onImportClick: (car: Car) => void,
  onDeleteAllClick: (id: string) => void,
  onDeleted: () => void
}) {
  const { slug } = useParams();
  const car = [...cars, ...sharedCars].find(c => c.slug === slug);

  if (!car) return null;

  return (
    <CarForm
      onBack={onBack}
      onCarSaved={onSaved}
      onCarDeleted={onDeleted}
      editingCar={car}
      onImportClick={() => onImportClick(car)}
      onDeleteAllClick={() => onDeleteAllClick(car.id)}
    />
  );
}

export default App;
